import React, { useEffect, useState, useCallback } from "react";
import * as Styled from "./ManageVersionsComponent.styled";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { blocksService } from "../../services";
import { Loader } from "../ui";
import { IVersion, BaseButton, ConfirmModal } from "../../libs";
import SearchIcon from "../../assets/images/icons/search.svg";
import { toast } from "react-toastify";
import { Plus, CloudUpload } from "react-bootstrap-icons";
import { ProfileCard } from "../manage-profiles-components/profile-card";
import { confirmAlert } from "react-confirm-alert";
import { NewVersionAlert } from "./new-version-alert";

export const ManageVersionsComponent: React.FC = () => {
    const navigate = useNavigate();
    const { profileId } = useParams<{ profileId: string }>();
    const { state } = useLocation();
    const [profile, setProfile] = useState(state?.profile || null);
    const [versions, setVersions] = useState<IVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchProfile = useCallback(async () => {
        if (!profileId || profile) return;

        try {
            const profiles = await blocksService.getProfiles();
            const currentProfile = profiles.find(p => p.profileId === profileId);
            if (currentProfile) {
                setProfile(currentProfile);
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            toast.error("Failed to load profile");
        }
    }, [profileId, profile]);

    const fetchVersions = useCallback(async () => {
        if (!profileId) return;

        setLoading(true);
        try {
            const data = await blocksService.getVersions(profileId);
            setVersions(data);
        } catch (error) {
            console.error("Error fetching versions:", error);
            toast.error("Failed to load versions");
        } finally {
            setLoading(false);
        }
    }, [profileId]);

    useEffect(() => {
        fetchProfile();
        fetchVersions();
    }, [fetchProfile, fetchVersions]);

    const filteredData = React.useMemo(() => {
        return versions.filter(item =>
            item.versionName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? true
        );
    }, [searchQuery, versions]);

    const handleVersionClick = (versionId: string) => {
        navigate(`/manage-libraries/manage-profiles/${profileId}/versions/${versionId}/blocks`, { state: { profile, version: versions.find(v => v.versionId === versionId) } });
    };

    const handleCreateVersion = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <NewVersionAlert
                        onClose={onClose}
                        handleSaveVersion={async (name: string) => {
                            if (!profileId) return;
                            try {
                                await blocksService.createVersion(profileId, { versionName: name });
                                toast.success("Version created successfully");
                                await fetchVersions();
                            } catch (error) {
                                console.error("Error creating version:", error);
                                toast.error("Failed to create version");
                            }
                        }}
                    />
                );
            },
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    };

    const showEditModal = React.useCallback((version: IVersion) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <NewVersionAlert
                        onClose={onClose}
                        handleSaveVersion={async (name: string) => {
                            if (!profileId) return;
                            try {
                                await blocksService.updateVersion(profileId, version.versionId, { versionId: version.versionId, versionName: name });
                                toast.success("Version updated successfully");
                                await fetchVersions();
                            } catch (error) {
                                console.error("Error updating version:", error);
                                toast.error("Failed to update version");
                            }
                        }}
                        initialVersionName={version.versionName}
                        isEdit={true}
                    />
                );
            },
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }, [fetchVersions, profileId]);

    const handleVersionActivationToggle = React.useCallback(async (version: IVersion, enable: boolean) => {
        if (!profileId) return;
        if (enable) {
            try {
                await blocksService.updateVersion(profileId, version.versionId, { versionId: version.versionId, isActive: 1 });
                toast.success("Version activated successfully");
                setVersions(prev => prev.map(v => ({ ...v, isActive: v.versionId === version.versionId ? 1 : 0 })));
            } catch (error) {
                console.error("Error activating version:", error);
                toast.error("Failed to activate version");
            }
        }
    }, [profileId]);

    const handlePublish = React.useCallback(() => {
        if (!profile) return;
        confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmModal
                    title="Publish Profile"
                    description={[
                        { text: `Invalidate cache for "${profile.profileName}"?` },
                        { text: "The next page load will fetch fresh data from the database.", bold: false },
                    ]}
                    noText="Cancel"
                    yesText="Publish"
                    onNo={onClose}
                    onYes={async () => {
                        onClose();
                        try {
                            await blocksService.invalidateProfileCache(profile.clientId, profile.profileId);
                            toast.success("Cache invalidated successfully");
                        } catch (error) {
                            console.error("Error invalidating cache:", error);
                            toast.error("Failed to invalidate cache");
                        }
                    }}
                />
            ),
            closeOnEscape: true,
            closeOnClickOutside: false,
        });
    }, [profile]);

    return loading ? (
        <Loader />
    ) : (
        <>
            <Styled.StyledContainer>
                <Styled.Header>
                    <Styled.HeaderLeftSection>
                        Versions of profile {profile?.profileName || profileId}
                    </Styled.HeaderLeftSection>
                    <Styled.HeaderRightSection>
                        <Styled.DashboardSearchWrapper>
                            <Styled.SearchBarWrapper>
                                <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                <Styled.SearchInput
                                    name="search"
                                    type="text"
                                    placeholder="Search by name"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </Styled.SearchBarWrapper>
                        </Styled.DashboardSearchWrapper>
                        <BaseButton onClick={handlePublish}><Styled.NewVersionWrapper><CloudUpload size={20} />Publish</Styled.NewVersionWrapper></BaseButton>
                        <BaseButton onClick={handleCreateVersion}><Styled.NewVersionWrapper>
                            <Plus size={28} />
                            New Version
                        </Styled.NewVersionWrapper></BaseButton>
                        <Styled.CustomButton variant="outline-secondary" onClick={() => navigate(-1)}>
                            Go Back
                        </Styled.CustomButton>
                    </Styled.HeaderRightSection>
                </Styled.Header>

                <Styled.Description>
                    Manage versions for this profile. Only one version can be active at a time.
                </Styled.Description>
                {
                    filteredData.length === 0 ? (
                        <Styled.NoDataWrapper>
                            No versions found. Create a new version to get started.
                        </Styled.NoDataWrapper>
                    ) : filteredData.map((version) => (
                        <ProfileCard
                            key={version.versionId}
                            title={version.versionName}
                            subtitle={`${version.blockCount || 0} blocks`}
                            isLinked={undefined}
                            isEnabled={version.isActive === 1}
                            onChevronRightClick={() => handleVersionClick(version.versionId)}
                            onEdit={() => {
                                showEditModal(version);
                            }}
                            onToggleEnabled={(enabled) => {
                                handleVersionActivationToggle(version, enabled);
                            }}
                        />
                    ))
                }
            </Styled.StyledContainer>
        </>
    );
};
